import { CardRewardsEarnRate, AdvancedCategory } from 'data/types/CardData';
import {
  isSelectableCategoriesEarnRate,
  isFixedCategoryGroupEarnRate,
  isQuarterlyCategoriesEarnRate,
} from 'data/types/utils';
import { formatSpendCategory } from '../data/shared/utils/formatSpendCategory';
import { getSelectedCategories } from '../data/shared/hooks/useBestEarnRatesV2';
import { joinWithAnd } from './listUtils';
import { getPromotionalTypeDisplay } from '../data/shared/promotionalTypes/promotionalTypeDisplayConfig';

// Helper function to extract end date from earn rate description
const extractEndDate = (description: string | null): string | null => {
  if (!description) {
    return null;
  }

  const endDatePatterns = [
    /valid through (\d{1,2}\/\d{1,2}\/\d{4})/i,
    /through (\d{1,2}\/\d{1,2}\/\d{4})/i,
    /until (\d{1,2}\/\d{1,2}\/\d{4})/i,
    /expires (\d{1,2}\/\d{1,2}\/\d{4})/i,
    /continues beyond (\d{1,2}\/\d{1,2}\/\d{4})/i,
    /excluded starting (\d{1,2}\/\d{1,2}\/\d{4})/i,
    /starting (\d{1,2}\/\d{1,2}\/\d{4})/i,
  ];

  for (const pattern of endDatePatterns) {
    const match = description.match(pattern);
    if (match) {
      return match[1];
    }
  }

  return null;
};

// Check if this earn rate is a Bilt housing rate with spend ratio promotions
export const isBiltHousingWithSpendRatio = (
  rate: CardRewardsEarnRate
): boolean => {
  if (rate.category !== 'housing') {
    return false;
  }
  if (!rate.conditionalTags?.includes('require_issuer_platform')) {
    return false;
  }
  if (!rate.promotions || rate.promotions.length === 0) {
    return false;
  }

  return rate.promotions.some((p) => {
    const promoType = p.promotional_type;
    return promoType && String(promoType).startsWith('bilt_spend_ratio');
  });
};

// Format frequency text
const formatFrequency = (
  frequency: string | null,
  compact: boolean
): string => {
  if (!frequency) {
    return '';
  }

  if (compact) {
    switch (frequency) {
      case 'monthly':
        return '/mo';
      case 'quarterly':
        return '/qtr';
      case 'biannually':
        return '/6mo';
      case 'annually':
        return '/yr';
      default:
        return '';
    }
  }

  switch (frequency) {
    case 'monthly':
      return 'monthly';
    case 'quarterly':
      return 'quarterly';
    case 'biannually':
      return 'every 6 months';
    case 'annually':
      return 'annually';
    default:
      return 'periodically';
  }
};

// Format spend cap amount (compact: no rounding — e.g. 1500 → $1.5k, 2000 → $2k)
const formatSpendCap = (amount: number, compact: boolean): string => {
  if (compact) {
    if (amount >= 1000) {
      return `$${amount / 1000}k`;
    }
    return `$${amount}`;
  }
  return `$${amount.toLocaleString()}`;
};

export type GenerateDisclaimersOptions = {
  compact?: boolean;
  showSharedCategories?: boolean;
  userSelectedCategories?: string[][];
  currentCategory?: string;
  /** The promotional type that produced this earn rate, if any */
  promotionalType?: string | null;
};

/**
 * Generates disclaimer strings for an earn rate.
 *
 * @param rate - The CardRewardsEarnRate to generate disclaimers for
 * @param options - Options for formatting
 * @returns Array of disclaimer strings
 */
export function generateDisclaimers(
  rate: CardRewardsEarnRate,
  options: GenerateDisclaimersOptions = {}
): string[] {
  const {
    compact = false,
    showSharedCategories = false,
    userSelectedCategories = [],
    currentCategory,
    promotionalType,
  } = options;

  const disclaimers: string[] = [];
  const isSelectable = isSelectableCategoriesEarnRate(rate);

  // Promotional type disclaimer (shown first as it's a key condition)
  if (promotionalType) {
    const displayText = getPromotionalTypeDisplay(
      promotionalType,
      compact ? 'short' : 'full'
    );
    disclaimers.push(displayText);
  }

  // Spend cap disclaimers
  if (rate.spendCap) {
    const resetFrequency = rate.spendCapResetFrequency;
    const capAmount = formatSpendCap(rate.spendCap, compact);

    if (compact) {
      // Short format for Reddit: "$1.5k/qtr cap" or "$1.5k/qtr shared cap"
      const freq = formatFrequency(resetFrequency, true);
      let capCategoryCount = 0;
      if ('selectableCategories' in rate && rate.selectableCategories) {
        const sel = rate.selectableCategories;
        capCategoryCount =
          sel.applicableCategoryCount ?? sel.categories?.length ?? 0;
      }
      const isSharedCap =
        (isSelectable && capCategoryCount > 1) ||
        (isFixedCategoryGroupEarnRate(rate) && capCategoryCount > 1);
      disclaimers.push(
        `${capAmount}${freq} ${isSharedCap ? 'shared ' : ''}cap`
      );
    } else {
      // Full format
      let resetText = '';
      if (resetFrequency) {
        if (isQuarterlyCategoriesEarnRate(rate)) {
          const endDate = extractEndDate(rate.description);
          resetText = endDate ? `until ${endDate}` : 'quarterly';
        } else {
          resetText = formatFrequency(resetFrequency, false);
        }
      }

      if (isSelectable && rate.selectableCategories?.categories) {
        // Cap applies to the selected category/categories, not the full pool to choose from.
        const capCategoryCount =
          rate.selectableCategories.applicableCategoryCount ??
          (rate.selectableCategories.categories.length > 0
            ? rate.selectableCategories.categories.length
            : 0);

        if (showSharedCategories) {
          const { applicableCategories } = getSelectedCategories({
            userSelectedCategories,
            reward: rate,
          });

          if (applicableCategories.length > 1) {
            const otherCategories = applicableCategories.filter((cat) => {
              const categoryName =
                typeof cat === 'string'
                  ? cat
                  : (cat as AdvancedCategory).category_description;
              return categoryName !== currentCategory;
            });

            if (otherCategories.length > 0) {
              const categoryNames = otherCategories.map((cat) =>
                formatSpendCategory(
                  typeof cat === 'string'
                    ? cat
                    : (cat as AdvancedCategory).category_description
                )
              );
              disclaimers.push(
                `${capAmount} limit shared with ${joinWithAnd(categoryNames)}${
                  resetFrequency
                    ? ` (${
                        isQuarterlyCategoriesEarnRate(rate)
                          ? resetText
                          : `resets ${resetText}`
                      })`
                    : ''
                }`
              );
            }
          } else if (capCategoryCount === 1) {
            disclaimers.push(
              `${capAmount} limit for this category${
                resetFrequency
                  ? ` (${
                      isQuarterlyCategoriesEarnRate(rate)
                        ? resetText
                        : `resets ${resetText}`
                    })`
                  : ''
              }`
            );
          } else if (capCategoryCount === 2) {
            disclaimers.push(
              `${capAmount} limit split between two categories${
                resetFrequency
                  ? ` (${
                      isQuarterlyCategoriesEarnRate(rate)
                        ? resetText
                        : `resets ${resetText}`
                    })`
                  : ''
              }`
            );
          } else if (capCategoryCount > 2) {
            disclaimers.push(
              `${capAmount} limit split between ${capCategoryCount} categories${
                resetFrequency
                  ? ` (${
                      isQuarterlyCategoriesEarnRate(rate)
                        ? resetText
                        : `resets ${resetText}`
                    })`
                  : ''
              }`
            );
          } else {
            disclaimers.push(
              `${capAmount} cap${
                resetFrequency
                  ? ` (${
                      isQuarterlyCategoriesEarnRate(rate)
                        ? resetText
                        : `resets ${resetText}`
                    })`
                  : ''
              }`
            );
          }
        } else {
          if (capCategoryCount === 1) {
            disclaimers.push(
              `${capAmount} limit for this category${
                resetFrequency
                  ? ` (${
                      isQuarterlyCategoriesEarnRate(rate)
                        ? resetText
                        : `resets ${resetText}`
                    })`
                  : ''
              }`
            );
          } else if (capCategoryCount === 2) {
            disclaimers.push(
              `${capAmount} limit split between two categories${
                resetFrequency
                  ? ` (${
                      isQuarterlyCategoriesEarnRate(rate)
                        ? resetText
                        : `resets ${resetText}`
                    })`
                  : ''
              }`
            );
          } else if (capCategoryCount > 2) {
            disclaimers.push(
              `${capAmount} limit split between ${capCategoryCount} categories${
                resetFrequency
                  ? ` (${
                      isQuarterlyCategoriesEarnRate(rate)
                        ? resetText
                        : `resets ${resetText}`
                    })`
                  : ''
              }`
            );
          } else {
            disclaimers.push(
              `${capAmount} cap${
                resetFrequency
                  ? ` (${
                      isQuarterlyCategoriesEarnRate(rate)
                        ? resetText
                        : `resets ${resetText}`
                    })`
                  : ''
              }`
            );
          }
        }
      } else if (
        isFixedCategoryGroupEarnRate(rate) &&
        rate.selectableCategories?.categories
      ) {
        const categoryCount = rate.selectableCategories.categories.length;

        if (showSharedCategories && categoryCount > 1) {
          const otherCategories = rate.selectableCategories.categories.filter(
            (cat) => {
              const categoryName =
                typeof cat === 'string'
                  ? cat
                  : (cat as AdvancedCategory).category_description;
              return categoryName !== currentCategory;
            }
          );

          if (otherCategories.length > 0) {
            const categoryNames = otherCategories.map((cat) =>
              formatSpendCategory(
                typeof cat === 'string'
                  ? cat
                  : (cat as AdvancedCategory).category_description
              )
            );
            disclaimers.push(
              `${capAmount} limit shared with ${joinWithAnd(categoryNames)}${
                resetFrequency
                  ? ` (${
                      isQuarterlyCategoriesEarnRate(rate)
                        ? resetText
                        : `resets ${resetText}`
                    })`
                  : ''
              }`
            );
          }
        } else {
          disclaimers.push(
            `${capAmount} cap${
              resetFrequency
                ? ` (${
                    isQuarterlyCategoriesEarnRate(rate)
                      ? resetText
                      : `resets ${resetText}`
                  })`
                : ''
            }`
          );
        }
      } else {
        disclaimers.push(
          `${capAmount} cap${
            resetFrequency
              ? ` (${
                  isQuarterlyCategoriesEarnRate(rate)
                    ? resetText
                    : `resets ${resetText}`
                })`
              : ''
          }`
        );
      }
    }
  }

  // Selection requirement (shown in both compact and full mode)
  if (isSelectable && rate.selectableCategories?.requireUserSelection) {
    disclaimers.push(compact ? 'requires selection' : 'Requires user selection');
  }

  // Skip additional disclaimers in compact mode for brevity
  if (compact) {
    return disclaimers;
  }

  // Minimum transaction amount
  if (rate.minimumTransactionAmountPerTransaction) {
    disclaimers.push(
      `Minimum transaction amount of $${rate.minimumTransactionAmountPerTransaction.toLocaleString()}`
    );
  }

  // Minimum transaction count
  if (rate.minimumTransactionCountPerCycle) {
    const countType = rate.minimumTransactionCountType;
    let scopeText = '';
    if (countType === 'across_all_categories') {
      scopeText = ' across all categories';
    } else if (countType === 'current_category') {
      scopeText = ' in this category';
    }

    disclaimers.push(
      `Minimum of ${rate.minimumTransactionCountPerCycle} transaction${
        rate.minimumTransactionCountPerCycle > 1 ? 's' : ''
      } required${scopeText}`
    );
  }

  // Spend minimum requirement
  if (rate.spendMinimum) {
    disclaimers.push(
      `Minimum spend of $${rate.spendMinimum.toLocaleString()} required`
    );
  }

  // Travel portal requirement
  if (rate.requiresTravelPortal) {
    disclaimers.push('Must book through travel portal');
  }

  // Excluded merchants
  if (rate.excludeMerchants && rate.excludeMerchants.length > 0) {
    disclaimers.push(`Excludes: ${rate.excludeMerchants.join(', ')}`);
  }

  if (
    isSelectable &&
    rate.selectableCategories?.applicableCategoryCount &&
    !showSharedCategories
  ) {
    disclaimers.push(
      `Can select ${rate.selectableCategories.applicableCategoryCount} categor${
        rate.selectableCategories.applicableCategoryCount === 1 ? 'y' : 'ies'
      }`
    );
  }

  // Rotation frequency
  if (isSelectable && rate.selectableCategories?.categoryRotationFrequency) {
    const rotationText = formatFrequency(
      rate.selectableCategories.categoryRotationFrequency,
      false
    );
    disclaimers.push(`Categories rotate ${rotationText}`);
  }

  // Selection change frequency
  if (isSelectable && rate.selectableCategories?.selectionChangeFrequency) {
    const changeText = formatFrequency(
      rate.selectableCategories.selectionChangeFrequency,
      false
    );
    disclaimers.push(`Can change selection ${changeText}`);
  }

  // End date information
  const endDate = extractEndDate(rate.description);
  if (endDate) {
    disclaimers.push(`Ends ${endDate}`);
  }

  // Conditional tags
  if (rate.conditionalTags && rate.conditionalTags.length > 0) {
    const isBiltHousing = isBiltHousingWithSpendRatio(rate);

    const filteredTags = rate.conditionalTags.filter(
      (tag) =>
        tag !== 'mystery_box' &&
        tag !== 'single_purchase_only' &&
        tag !== 'require_minimum_transaction_amount' &&
        tag !== 'spend_minimum_required' &&
        tag !== 'spend_cap_applies' &&
        tag !== 'exclude_specific_merchants' &&
        tag !== 'include_specific_merchants' &&
        tag !== 'require_minimum_transaction_count' &&
        tag !== 'require_travel_portal' &&
        tag !== 'earn_when_buy' &&
        tag !== 'earn_when_fly' &&
        !(isBiltHousing && tag === 'require_issuer_platform')
    );

    if (filteredTags.length > 0) {
      const existingEndDate = extractEndDate(rate.description);
      const finalFilteredTags = existingEndDate
        ? filteredTags.filter((tag) => !tag.includes('time_limited'))
        : filteredTags;

      const conditionalTexts = finalFilteredTags.map((tag) => {
        switch (tag) {
          case 'require_travel_portal':
            return 'Must book through travel portal';
          case 'require_paying_account_balance':
            return 'Requires paying account balance';
          case 'time_limited_24_hours':
            return '24-hour time limit';
          case 'time_limited_7_days':
            return '7-day time limit';
          case 'require_qualifying_bank_balance':
            return 'Requires qualifying bank balance';
          case 'us_purchases_only':
            return 'U.S. purchases only';
          default:
            return tag
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (l) => l.toUpperCase());
        }
      });

      if (finalFilteredTags.length > 0) {
        if (conditionalTexts.length === 1) {
          disclaimers.push(conditionalTexts[0]);
        } else if (conditionalTexts.length > 1) {
          disclaimers.push(`Conditions: ${conditionalTexts.join(', ')}`);
        }
      }
    }
  }

  // Qualifying bank balance requirement
  if (rate.requireQualifyingBankBalanceAmount) {
    disclaimers.push(
      `Requires $${rate.requireQualifyingBankBalanceAmount.toLocaleString()} qualifying bank balance`
    );
  }

  return disclaimers;
}
